import { useEffect, useState } from "react";
import { ArrowUpRight, X } from "lucide-react";
import { ENABLE_I18N, LANGUAGES, useI18n } from "@/lib/i18n";

const STORAGE_KEY = "fk_lang_suggested_v1";

/**
 * Region-aware suggestion banner.
 * On first visit only: detect navigator.language and — if it differs from the
 * currently active site language and is among our active languages — offer a
 * subtle one-click switch. Dismissed permanently via localStorage.
 */
const COPY = {
  fr: {
    flag: "fr",
    msg: "Bonjour ! Cette page est aussi disponible en français.",
    cta: "Voir en français",
  },
  en: {
    flag: "en",
    msg: "Hello! This page is also available in English.",
    cta: "View in English",
  },
  de: {
    flag: "de",
    msg: "Grüezi! Diese Seite ist auch auf Deutsch verfügbar.",
    cta: "Auf Deutsch ansehen",
  },
  it: {
    flag: "it",
    msg: "Buongiorno! Questa pagina è disponibile anche in italiano.",
    cta: "Visualizza in italiano",
  },
};

const FlagMini = ({ code }) => {
  const cls = "block w-full h-full";
  if (code === "fr")
    return (
      <svg viewBox="0 0 3 2" className={cls} preserveAspectRatio="none" aria-hidden="true">
        <rect width="1" height="2" fill="#002654" />
        <rect x="1" width="1" height="2" fill="#fff" />
        <rect x="2" width="1" height="2" fill="#ED2939" />
      </svg>
    );
  if (code === "en")
    return (
      <svg viewBox="0 0 60 30" className={cls} preserveAspectRatio="none" aria-hidden="true">
        <clipPath id="fk-sg-uk">
          <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" />
        </clipPath>
        <rect width="60" height="30" fill="#012169" />
        <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
        <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#fk-sg-uk)" stroke="#C8102E" strokeWidth="4" />
        <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
        <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
      </svg>
    );
  if (code === "it")
    return (
      <svg viewBox="0 0 3 2" className={cls} preserveAspectRatio="none" aria-hidden="true">
        <rect width="1" height="2" fill="#009246" />
        <rect x="1" width="1" height="2" fill="#fff" />
        <rect x="2" width="1" height="2" fill="#CE2B37" />
      </svg>
    );
  if (code === "de")
    return (
      <svg viewBox="0 0 5 3" className={cls} preserveAspectRatio="none" aria-hidden="true">
        <rect width="5" height="1" fill="#000" />
        <rect y="1" width="5" height="1" fill="#D00" />
        <rect y="2" width="5" height="1" fill="#FFCE00" />
      </svg>
    );
  return null;
};

export default function LanguageSuggestion() {
  const { lang, setLang } = useI18n();
  const [suggest, setSuggest] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!ENABLE_I18N) return;
    if (typeof window === "undefined") return;
    if (localStorage.getItem(STORAGE_KEY)) return;

    const browserLang = (navigator.language || "").toLowerCase().split("-")[0];
    const available = LANGUAGES.map((l) => l.code);
    if (!available.includes(browserLang)) return;
    if (browserLang === lang) return;
    if (!COPY[browserLang]) return;

    setSuggest(browserLang);
    // Delay so it doesn't appear during initial load splash
    const t = setTimeout(() => setShow(true), 1800);
    return () => clearTimeout(t);
  }, [lang]);

  const dismiss = () => {
    setShow(false);
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch (_) { /* ignore */ }
  };

  const accept = () => {
    if (suggest) setLang(suggest);
    dismiss();
  };

  if (!ENABLE_I18N || !suggest || !show) return null;
  const c = COPY[suggest];

  return (
    <div
      data-testid="lang-suggestion"
      role="region"
      aria-label="Language suggestion"
      className="fixed z-[55] top-24 right-6 max-w-[360px] animate-[fadeIn_0.5s_ease-out]"
    >
      <div className="relative bg-[#0E0E10]/95 backdrop-blur-md border border-white/15 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.7)] p-4 pr-10">
        <button
          type="button"
          onClick={dismiss}
          data-testid="lang-suggestion-close"
          aria-label="Dismiss"
          className="absolute top-2 right-2 p-1 text-zinc-500 hover:text-white transition-colors"
        >
          <X className="w-3.5 h-3.5" strokeWidth={1.5} />
        </button>

        <div className="flex items-start gap-3">
          <span className="block flex-none w-8 h-[22px] mt-0.5 ring-1 ring-white/20 overflow-hidden">
            <FlagMini code={c.flag} />
          </span>
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">
              {suggest === "fr" ? "Romandie" : suggest === "it" ? "Ticino" : suggest === "en" ? "International" : "Region"}
            </div>
            <p className="text-sm text-zinc-200 leading-snug mb-3">
              {c.msg}
            </p>
            <button
              type="button"
              onClick={accept}
              data-testid="lang-suggestion-accept"
              className="group inline-flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-[0.14em] text-white border-b border-white/40 hover:border-white pb-0.5 transition-colors"
            >
              {c.cta}
              <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        <div className="absolute -top-1 left-4 w-2 h-2 bg-white rounded-full pulse-dot" />
      </div>
    </div>
  );
}
